import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, Plus, Mail, Phone, ExternalLink, Star } from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import type { ContactRecord } from "@/types";

interface ContactsWidgetProps {
  contacts: ContactRecord[];
  onAddContact?: () => void;
  onWhatsAppContact?: (contact: ContactRecord) => void;
  canCreate?: boolean;
}

export function ContactsWidget({
  contacts,
  onAddContact,
  onWhatsAppContact,
  canCreate,
}: ContactsWidgetProps) {
  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center shrink-0">
            <Users className="size-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold tracking-tight text-foreground">
              Contacts ({contacts.length})
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Key stakeholders and representatives
            </p>
          </div>
        </div>

        {canCreate && onAddContact && (
          <Button
            type="button"
            size="sm"
            onClick={onAddContact}
            className="gap-1.5 h-8 text-xs font-medium"
          >
            <Plus className="size-3.5" />
            <span>Add Contact</span>
          </Button>
        )}
      </div>

      {/* Table / List */}
      {contacts.length === 0 ? (
        <div className="p-8 text-center space-y-3">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <Users className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No contacts yet</p>
            <p className="text-xs text-muted-foreground">
              No contacts have been added under this account.
            </p>
          </div>
          {canCreate && onAddContact && (
            <Button type="button" variant="outline" size="sm" onClick={onAddContact} className="gap-1.5 text-xs">
              <Plus className="size-3.5" />
              <span>Add First Contact</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead className="text-xs font-semibold">Contact Name</TableHead>
                <TableHead className="text-xs font-semibold">Role / Title</TableHead>
                <TableHead className="text-xs font-semibold">Email</TableHead>
                <TableHead className="text-xs font-semibold">Phone / Mobile</TableHead>
                <TableHead className="text-xs font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => {
                const phoneNum = contact.mobile || contact.phone;
                return (
                  <TableRow key={contact.id} className="hover:bg-muted/40 transition-colors">
                    <TableCell className="font-medium text-xs py-3">
                      <div className="flex items-center gap-1.5">
                        <Link
                          to={`/contacts/${contact.id}`}
                          className="text-foreground hover:text-primary font-semibold hover:underline inline-flex items-center gap-1 group"
                        >
                          <span>{contact.first_name} {contact.last_name || ""}</span>
                          <ExternalLink className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        {contact.is_primary && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 gap-1">
                            <Star className="size-2.5 fill-current" />
                            Primary
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground py-3">
                      {contact.job_title || "—"}
                    </TableCell>

                    <TableCell className="text-xs py-3">
                      {contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-foreground/80 hover:text-primary inline-flex items-center gap-1 hover:underline"
                        >
                          <Mail className="size-3 text-muted-foreground shrink-0" />
                          <span>{contact.email}</span>
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-xs py-3">
                      {phoneNum ? (
                        <a
                          href={`tel:${phoneNum}`}
                          className="text-foreground/80 hover:text-primary inline-flex items-center gap-1 hover:underline font-mono"
                        >
                          <Phone className="size-3 text-muted-foreground shrink-0" />
                          <span>{phoneNum}</span>
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-xs py-3 text-right">
                      {phoneNum && onWhatsAppContact && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => onWhatsAppContact(contact)}
                          className="size-7 text-[#128C7E] dark:text-[#25D366] hover:bg-[#25D366]/10 rounded-lg"
                          title="Chat on WhatsApp"
                        >
                          <WhatsAppIcon className="size-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
